import { FC } from 'react';
import { getCurrentEnvironment } from '@belong/env';
import { BREAKPOINTS, BREAKPOINTS_MAX_WIDTH } from '@belong/themes';
import {
  BasicImageSource,
  ContentfulImageOptions,
  ENVS,
  IMAGE_FORMAT,
  ImageContentType,
  ISource,
  ResponsiveImageSource,
  TBreakpointKeys
} from '@belong/types';
import Picture from '../../styles/Picture';
import { IPictureContainer } from './PictureContainer.types';

export const retinaMultiplier = 2;
export const imageQuality = 70;

const PictureContainer: FC<IPictureContainer> = ({
  isOptimized = true,
  alt,
  src,
  contentType,
  isPresentation,
  className,
  loading,
  imageAriaHidden,
  fetchPriority
}: IPictureContainer) => {
  if (getCurrentEnvironment() === ENVS.DEVELOPMENT) {
    if (isPresentation) {
      // eslint-disable-next-line no-console
      console.warn(
        'Deprecated usage of `isPresentation` property on <PictureContainer> component. Just pass a falsy `alt` text prop instead (or omit `alt` entirely).'
      );
    }
  }

  const performanceEnhancements = (options: ContentfulImageOptions): ContentfulImageOptions => {
    const newOptions: ContentfulImageOptions = {};

    if (contentType === 'svg') {
      return {};
    }

    if (options.w || options.h) {
      newOptions.q = imageQuality;
    }

    if (options.w) {
      newOptions.w = retinaMultiplier * options.w;
    }

    if (options.h) {
      newOptions.h = retinaMultiplier * options.h;
    }

    return { ...options, ...newOptions };
  };

  const decorativeAttributes = (): { alt: string; 'aria-hidden'?: boolean } => {
    const attribute = {
      alt: alt || ''
    };
    if (imageAriaHidden) {
      return { ...attribute, 'aria-hidden': true };
    }

    return attribute;
  };

  const srcToString = ([url, options]: BasicImageSource): string => {
    if (!url || url === '') {
      return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    }

    const newOptions = isOptimized ? performanceEnhancements(options ?? {}) : (options ?? {});

    if (Object.keys(newOptions).length === 0) {
      return url;
    }

    return `${url}?${Object.keys(newOptions)
      .map(k => `${k}=${newOptions[k as keyof ContentfulImageOptions]}`)
      .join('&')}`;
  };

  const compileResponsiveSrc = (srcObject: ResponsiveImageSource): ISource[] => {
    const srcKeys = Object.keys(srcObject);
    const lastKeyIndex = srcKeys.length - 1;
    const formats: Array<ImageContentType> = [IMAGE_FORMAT.avif, IMAGE_FORMAT.webp];

    const result: ISource[] = [];

    srcKeys.forEach((k, index) => {
      const key = k as TBreakpointKeys;
      const source = srcObject[key];
      const media =
        index !== lastKeyIndex && key !== 'xl'
          ? `(max-width: ${BREAKPOINTS_MAX_WIDTH[key] / 16}em)`
          : `(min-width: ${BREAKPOINTS[key] / 16}em)`;

      if (!source || source.length === 0) {
        result.push({
          media,
          src: srcToString([]),
          mimeType: `image/${IMAGE_FORMAT.gif}`
        });

        return;
      }

      formats.forEach(format => {
        const [url, options] = source;
        result.push({
          media,
          src: srcToString([url ?? '', { ...options, fm: format }]),
          mimeType: `image/${IMAGE_FORMAT[format]}`
        });
      });
    });

    return result;
  };

  const compileSrc = (): string | ISource[] => {
    if (Array.isArray(src)) {
      return srcToString(src);
    }

    return compileResponsiveSrc(src);
  };

  const source = compileSrc();

  const decorativeAttrs = decorativeAttributes();

  return (
    <Picture
      loading={loading}
      className={className}
      source={source}
      {...decorativeAttrs}
      fetchPriority={fetchPriority}
    />
  );
};

PictureContainer.displayName = 'PictureContainer';
export { PictureContainer };
